import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import RestartAlt from "@mui/icons-material/RestartAlt";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { mdHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  NotificationPreferenceAudience,
  NotificationPreferenceScope,
} from "generated-graphql/graphql";
import { useCallback, useState } from "react";

import {
  MY_NOTIFICATION_PREFERENCE_CATALOG,
  MY_SUBSCRIBED_ORGANIZATIONS,
  SET_TENANT_NOTIFICATION_SUBSCRIPTION,
  writeSubscribedOrganizations,
} from "./notificationPreferenceDocuments";
import { UNMUTE_CONFIRM_BODY, UNMUTE_CONFIRM_HEADER } from "./preferenceProvenance";
import { usePreferenceActions } from "./preferencesPageState";
import { PreferencesTabPanel } from "./PreferencesPanel";
import {
  ALL_ORGANIZATIONS,
  SubscribedOrganizationList,
  type OrganizationSelection,
} from "./SubscribedOrganizationList";
import { useAutosavingDraft } from "./useAutosavingDraft";

/**
 * The staff tab, in place of "My preferences": a staff member's own settings for
 * every section, Encamp-only alerts included, for every organization or for one.
 *
 * "All organizations" edits the rows that apply everywhere. An organization edits
 * that organization's own rows, and anything left alone there follows the
 * all-organizations setting — which is what each row's explanation says. From an
 * organization they are not subscribed to, only notifications about their own work
 * reach a staff member.
 *
 * Every change saves as it is made (see {@link useAutosavingDraft}), so there is no
 * Save button and nothing unsaved to lose by moving to another organization.
 */

const PANEL_MAX_WIDTH = 840;

/**
 * Below this the list sits above the panel: beside it, the list's fixed width would
 * squeeze the switch grid under its minimum and the page would scroll sideways.
 */
const SIDE_BY_SIDE_FROM = "lg";

const LIST_WIDTH = 280;

const RESET_COPY = {
  all: {
    button: "Reset to Encamp defaults",
    header: "Reset your default notification preferences?",
    body: "Your defaults will be cleared, so Encamp's recommended settings apply wherever you haven't overridden them for an organization. This can't be undone.",
  },
  organization: {
    button: "Use my defaults",
    header: "Use your defaults for this organization?",
    body: "Your choices for this organization will be cleared, so it follows your defaults. This can't be undone.",
  },
} as const;

function useSubscribedOrganizations() {
  const alerts = useAlerts();
  const query = useQuery(MY_SUBSCRIBED_ORGANIZATIONS);
  const [setSubscription, { loading: changing }] = useMutation(
    SET_TENANT_NOTIFICATION_SUBSCRIPTION,
    {
      onError: (error) => alerts.error(error.message),
      update: writeSubscribedOrganizations,
    }
  );

  /** True once the change is saved; a failure has already been reported. */
  const onSubscribe = useCallback(
    async (tenantId: string, subscribed: boolean): Promise<boolean> => {
      const result = await setSubscription({ variables: { tenantId, subscribed } });
      return Boolean(result.data);
    },
    [setSubscription]
  );

  return {
    organizations: query.data?.mySubscribedOrganizations,
    loading: query.loading,
    changing,
    onSubscribe,
  };
}

/**
 * One organization's settings, or the Default's. Mounted afresh for each selection,
 * so a save still landing for one can never show up in another's switches.
 */
function StaffPreferencesPanel({
  tenantId,
  heading,
  description,
}: {
  /** Null is the Default, which applies in every organization. */
  tenantId: string | null;
  heading: string;
  description?: string;
}) {
  const [resetOpen, setResetOpen] = useState(false);
  const closeReset = useCallback(() => setResetOpen(false), []);

  const catalogQuery = useQuery(MY_NOTIFICATION_PREFERENCE_CATALOG, {
    variables: {
      tenantId,
      scope: NotificationPreferenceScope.My,
      audience: NotificationPreferenceAudience.Staff,
    },
    // A change to the Default, or a save still landing when the reader moved on,
    // leaves other organizations' cached settings stale.
    fetchPolicy: "cache-and-network",
  });
  const catalog = catalogQuery.data?.myNotificationPreferenceCatalog;
  const autosave = useAutosavingDraft({ tenantId, catalog });
  const client = useApolloClient();
  // A reset clears an organization's own settings, which the list marks.
  const refetchCatalog = useCallback(async () => {
    await Promise.all([
      catalogQuery.refetch(),
      client.refetchQueries({ include: [MY_SUBSCRIBED_ORGANIZATIONS] }),
    ]);
  }, [catalogQuery, client]);

  const actions = usePreferenceActions({
    tenantId,
    audience: NotificationPreferenceAudience.Staff,
    scope: NotificationPreferenceScope.My,
    activeController: autosave.controller,
    sections: catalog?.sections,
    channelParentSwitches: catalog?.channelParentSwitches,
    refetchBoth: refetchCatalog,
    refetchMine: refetchCatalog,
    closeReset,
    closePreset: closeReset,
  });
  const resetCopy = tenantId === null ? RESET_COPY.all : RESET_COPY.organization;

  return (
    <>
      {catalogQuery.error ? (
        <Alert severity="error">
          Failed to load your notification settings: {catalogQuery.error.message}
        </Alert>
      ) : (
        <PreferencesTabPanel
          catalog={catalog}
          controller={autosave.controller}
          scope={NotificationPreferenceScope.My}
          disabled={actions.mutating}
          pendingKeys={autosave.pendingKeys}
          heading={heading}
          description={description}
          showOverrides={tenantId !== null}
          // Beside the heading, so autosave's only feedback is in view when it matters.
          action={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {autosave.saving ? "Saving…" : "Changes are saved automatically"}
              </Typography>
              <Button
                size="small"
                startIcon={<RestartAlt />}
                // A reset racing a save could leave either one's rows behind.
                disabled={actions.mutating || autosave.saving || !catalog}
                onClick={() => setResetOpen(true)}
              >
                {resetCopy.button}
              </Button>
            </Stack>
          }
        />
      )}

      <ConfirmDialog
        open={resetOpen}
        header={resetCopy.header}
        info={resetCopy.body}
        cancelText="Cancel"
        confirmText={resetCopy.button}
        loading={actions.resetting}
        onClose={closeReset}
        onConfirm={actions.handleReset}
      />
      <ConfirmDialog
        open={autosave.confirmation.open}
        header={UNMUTE_CONFIRM_HEADER}
        info={UNMUTE_CONFIRM_BODY[NotificationPreferenceScope.My]}
        cancelText="Cancel"
        confirmText="Turn on"
        onClose={autosave.confirmation.cancel}
        onConfirm={autosave.confirmation.confirm}
      />
    </>
  );
}

export function EncampAlertsTab({
  currentTenant,
  selection,
  onSelect,
}: {
  /** The organization the page is open in. */
  currentTenant: { id: string; name: string } | undefined;
  selection: OrganizationSelection;
  onSelect: (selection: OrganizationSelection) => void;
}) {
  const subscribedOrganizations = useSubscribedOrganizations();
  const [unsubscribing, setUnsubscribing] = useState<{ tenantId: string; name: string } | null>(
    null
  );

  const everyOrganization = selection === ALL_ORGANIZATIONS;
  const tenantId = everyOrganization ? null : selection;
  const selectedName =
    subscribedOrganizations.organizations?.find(
      (organization) => organization.tenantId === tenantId
    )?.name ?? (tenantId === currentTenant?.id ? currentTenant?.name : undefined);

  const notSubscribedHere =
    tenantId !== null &&
    subscribedOrganizations.organizations !== undefined &&
    !subscribedOrganizations.organizations.some(
      (organization) => organization.tenantId === tenantId
    );

  // Unsubscribing from the selected organization drops it from the list, unless it is
  // the one the page is open in; the panel goes back to Default rather than keep
  // editing an organization the list no longer shows.
  const { onSubscribe } = subscribedOrganizations;
  const handleSubscribe = useCallback(
    async (subscribeTenantId: string, subscribed: boolean) => {
      const saved = await onSubscribe(subscribeTenantId, subscribed);
      if (
        saved &&
        !subscribed &&
        subscribeTenantId === tenantId &&
        subscribeTenantId !== currentTenant?.id
      ) {
        onSelect(ALL_ORGANIZATIONS);
      }
    },
    [currentTenant?.id, onSelect, onSubscribe, tenantId]
  );

  // A one-click unsubscribe stops everything from the organization, so it asks first.
  const askToSubscribe = useCallback(
    async (subscribeTenantId: string, subscribed: boolean) => {
      if (subscribed) {
        await handleSubscribe(subscribeTenantId, true);
        return;
      }
      const name =
        subscribedOrganizations.organizations?.find(
          (organization) => organization.tenantId === subscribeTenantId
        )?.name ?? "this organization";
      setUnsubscribing({ tenantId: subscribeTenantId, name });
    },
    [handleSubscribe, subscribedOrganizations.organizations]
  );
  const confirmUnsubscribe = useCallback(async () => {
    if (unsubscribing) {
      await handleSubscribe(unsubscribing.tenantId, false);
      setUnsubscribing(null);
    }
  }, [handleSubscribe, unsubscribing]);

  const panelTitle = everyOrganization ? "Default" : (selectedName ?? "");
  const panelSubtitle = everyOrganization
    ? "These apply in every subscribed organization where you haven't overridden it."
    : undefined;

  return (
    <Stack
      direction={{ xs: "column", [SIDE_BY_SIDE_FROM]: "row" }}
      spacing={2}
      alignItems={{ xs: "stretch", [SIDE_BY_SIDE_FROM]: "flex-start" }}
    >
      <Box
        sx={{
          width: { xs: "100%", [SIDE_BY_SIDE_FROM]: LIST_WIDTH },
          maxWidth: PANEL_MAX_WIDTH,
          flexShrink: 0,
          // Kept in reach on a long page, just under the app bar.
          position: { [SIDE_BY_SIDE_FROM]: "sticky" },
          top: (theme) => `calc(${theme.spacing(mdHeight)} + ${theme.spacing(2)})`,
          zIndex: 1,
        }}
      >
        <SubscribedOrganizationList
          organizations={subscribedOrganizations.organizations}
          loading={subscribedOrganizations.loading}
          currentTenant={currentTenant}
          selection={selection}
          disabled={subscribedOrganizations.changing}
          onSelect={onSelect}
          onSubscribe={askToSubscribe}
        />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: PANEL_MAX_WIDTH }}>
        {notSubscribedHere && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You aren't subscribed to {selectedName ?? "this organization"}, so only notifications
            about your own work there reach you, like exports you start and tasks assigned to you.
            Subscribe to it from the list to get the rest.
          </Alert>
        )}
        <StaffPreferencesPanel
          key={selection}
          tenantId={tenantId}
          heading={panelTitle}
          description={panelSubtitle}
        />
      </Box>

      <ConfirmDialog
        open={unsubscribing !== null}
        header={`Unsubscribe from ${unsubscribing?.name ?? ""}?`}
        info={`You'll stop getting ${unsubscribing?.name ?? "this organization"}'s notifications, except ones about your own work there, like exports you start and tasks assigned to you. You can subscribe again with Add organization.`}
        cancelText="Cancel"
        confirmText="Unsubscribe"
        loading={subscribedOrganizations.changing}
        onClose={() => setUnsubscribing(null)}
        onConfirm={confirmUnsubscribe}
      />
    </Stack>
  );
}
